import type { StyleElements } from "replo-runtime/shared/utils/styles";
import type { CustomPropsRecord } from "schemas/component";
import type { ComponentConfig } from "../../components";

import { convertToLegacyProps } from "../../../shared/utils/props";

function getConfigurableProps(): CustomPropsRecord {
  return {
    _option: {
      name: "Option",
      type: "productOptions",
      defaultValue: null,
    },
    _hideDefaultArrowDisplay: {
      name: "Hide Default Arrow Display",
      type: "boolean",
      defaultValue: false,
      description:
        "If set, the default arrow which indicates the component is a dropdown will be hidden.",
    },
    _defaultText: {
      name: "Default Text",
      type: "string",
      defaultValue: null,
      description:
        "If set, this text will be displayed when the user has never selected an option.",
    },
    _swatches: {
      type: "swatches",
      name: "Swatches",
      description: "",
      defaultValue: null,
    },
    _showOptionsNotSoldTogether: {
      name: "Show Options Not Sold Together",
      type: "boolean",
      description:
        "Show all options, even if they are not sold together with the other selected options",
      defaultValue: false,
    },
    _accessibilityHidden: {
      type: "boolean",
      name: "Accessibility Hidden",
      description:
        "If set, removes this component from the accessibility tree, hiding it from screen readers.",
      defaultValue: null,
    },
    _accessibilityLabelledBy: {
      type: "selectable",
      name: "Accessibility Labelled By",
      description:
        "If you select a Text component here, the Text component will be used to label this component in accessibility controls.",
      defaultValue: null,
      placeholder: "Select a component",
    },
    _accessibilityLabel: {
      type: "string",
      name: "Accessibility Label",
      description:
        "If provided, this will be used as the aria-label for this component. Defaults to the option name.",
      defaultValue: undefined,
    },
  };
}

const styleElements = {
  root: {
    defaultStyles: {
      cursor: "pointer",
      borderTopWidth: 0,
      borderBottomWidth: 0,
      borderRightWidth: 0,
      borderLeftWidth: 0,
    },
    overrideStyles({ component }) {
      const hideDefaultArrowDisplay = component.props._hideDefaultArrowDisplay;
      return hideDefaultArrowDisplay ? { appearance: "none" } : {};
    },
  },
} as const satisfies StyleElements;

const config = {
  renderData: {
    customProps: convertToLegacyProps(getConfigurableProps()),
    newInstancesUseNumbering: true,
    acceptsArbitraryChildren: () => false,
    isAlwaysDynamic: true,
    canvasIndicatorDragDirections: [],
    ancestorAllow: {
      ancestorTypes: ["product"],
      message:
        "Option Dropdown components may only be placed inside a Product box.",
    },
    ancestorDisallowList: [
      {
        ancestorTypes: ["marquee"],
        message: "Option Dropdowns cannot be nested inside tickers.",
      },
    ],
    allowsLayoutModification: false,
    canContainChildren: false,
    showAccessibilityMenu: true,
    styleElements,
  },
} satisfies ComponentConfig;

export default config;
